import useRequest from "@/lib/request/useRequest";
import { useMemo, useCallback, useEffect, useState } from "react";
import { RxType } from "@/types/enums";
import { Rx } from "@/types/rx";
import { Feature } from "geojson";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    SharedSelection,
    SortDescriptor,
} from "@heroui/react";
import NumberInput from "@/components/inputs/NumberInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faDroplet,
    faDropletSlash,
    faCheck,
    faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { InferType, object, string, number, array } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { FormProvider, useForm } from "react-hook-form";
import { debounce } from "lodash";
import RxTableToolbar from "./RxTableToolbar";
import useAuth from "@/lib/auth/useAuth";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

const schema = object().shape({
    rate: string().oneOf(["n", "product"]).required(),
    rows: array()
        .of(
            object().shape({
                row: number().required(),
                target_rate_n: number().required(),
                product_rate: number().required(),
                actual_rate_n: number().optional(),
                actual_product_rate: number().optional(),
                rate: number().optional(),
            }),
        )
        .required(),
});
type FormType = InferType<typeof schema>;

function RxTable({
    rx,
    onUpdate,
}: {
    rx: Rx;
    onUpdate?: (updatedRx: Rx) => void;
}) {
    const { t } = useTranslation();
    const { userIsSentinel } = useAuth();
    const { useMutateRequest } = useRequest();
    const updateRates = useMutateRequest<Rx>({
        url: `/prescriptions/${rx.id}/table/`,
        method: "put",
        successMessage: t("success.rates_updated_successfully", {
            ns: "messages",
        }),
        errorMessage: t("error.failed_to_update_rates", { ns: "messages" }),
    });
    const form = useForm<FormType>({
        resolver: yupResolver(schema),
        mode: "onBlur",
    });
    useEffect(() => {
        if (rx.table) {
            form.reset({
                rate: "n",
                rows: rx.table.features.map((feature: Feature) => ({
                    row: Number(feature?.id),
                    target_rate_n:
                        feature.properties?.target_rate_n?.toFixed(2),
                    product_rate: feature.properties?.product_rate?.toFixed(2),
                    actual_rate_n:
                        feature.properties?.actual_rate_n?.toFixed(2),
                    actual_product_rate:
                        feature.properties?.actual_product_rate?.toFixed(2),
                })),
            });
        }
    }, [rx.table, form]);

    const zones = useMemo(() => {
        if (!rx.table) return [];
        return Array.from(
            new Set(
                rx.table.features
                    .map((feature: Feature) => feature.properties?.zone)
                    .filter((zone: number) => !!zone),
            ),
        ).sort((a: number, b: number) => a - b);
    }, [rx.table]);
    const treatments = useMemo(() => {
        if (!rx.table) return [];
        return Array.from(
            new Set(
                rx.table.features
                    .map((feature: Feature) => feature.properties?.treatment)
                    .filter((treatment: string) => !!treatment),
            ),
        ).sort((a: string, b: string) => a.localeCompare(b));
    }, [rx.table]);

    const { formatUnit } = useI18n();
    const columns = useMemo(() => {
        if (!rx.table) return [];
        const columns =
            zones.length > 0
                ? [{ key: "zone", label: t("zone", { ns: "rx" }) }]
                : [];
        if (treatments.length > 0) {
            columns.push({
                key: "treatment",
                label: t("treatment", { ns: "rx" }),
            });
        }
        if (rx.rx_type === RxType.ZONE) {
            columns.push(
                { key: "irrigated", label: t("irrigated", { ns: "rx" }) },
                {
                    key: "target_rate_n",
                    label: `${t("n_rate", { ns: "rx" })} (${formatUnit("lb_ac", "kg_ha")})`,
                },
                { key: "product_rate", label: t("product_rate", { ns: "rx" }) },
                { key: "area", label: t("area", { ns: "rx" }) },
            );
        } else {
            columns.push(
                { key: "start", label: t("start", { ns: "rx" }) },
                { key: "stop", label: t("stop", { ns: "rx" }) },
                {
                    key: "target_rate_n",
                    label: `${t("n_rate", { ns: "rx" })} (${formatUnit("lb_ac", "kg_ha")})`,
                },
                {
                    key: "actual_product_rate",
                    label: t("product_rate", { ns: "rx" }),
                },
            );
            if (rx.rx_type === RxType.VRF) {
                columns.push({
                    key: "injection_rate",
                    label: t("injection_rate", { ns: "rx" }),
                });
            } else if (rx.rx_type === RxType.VRI) {
                columns.push(
                    { key: "speed", label: t("speed", { ns: "rx" }) },
                    { key: "depth", label: t("depth", { ns: "rx" }) },
                );
            }
            columns.push({ key: "end_gun", label: t("end_gun", { ns: "rx" }) });
            if (userIsSentinel) {
                columns.push({
                    key: "actual_rate_n",
                    label: t("actual_rate_n", { ns: "rx" }),
                });
                columns.push({
                    key: "actual_product_rate",
                    label: t("actual_product_rate", { ns: "rx" }),
                });
            }
        }
        return columns;
    }, [
        rx.table,
        rx.rx_type,
        zones.length,
        t,
        treatments.length,
        formatUnit,
        userIsSentinel,
    ]);

    const handleUpdateSuccess = useCallback(
        (updatedRx: Rx) => {
            form.reset({
                rate: "n",
                rows: updatedRx.table.features.map((feature: Feature) => ({
                    row: Number(feature?.id),
                    target_rate_n: feature.properties?.target_rate_n,
                    product_rate: feature.properties?.product_rate,
                    actual_rate_n: feature.properties?.actual_rate_n,
                    actual_product_rate:
                        feature.properties?.actual_product_rate,
                })),
            });
            if (onUpdate) {
                onUpdate(updatedRx);
            }
        },
        [onUpdate, form],
    );
    const handleCellUpdate = debounce(
        (
            feature: Feature,
            columnKey:
                | "target_rate_n"
                | "product_rate"
                | "actual_rate_n"
                | "actual_product_rate",
        ) => {
            const values = form.getValues();
            const rateToUpdate =
                columnKey === "target_rate_n" || columnKey === "actual_rate_n"
                    ? "n"
                    : "product";
            const rate = Number(
                values.rows.find((row) => row.row === Number(feature.id))?.[
                columnKey
                ],
            );
            updateRates.mutate(
                {
                    data: {
                        rate: rateToUpdate,
                        rows: [
                            {
                                row: Number(feature.id),
                                rate: rate,
                            },
                        ],
                    },
                },
                {
                    onSuccess: (updatedRx: Rx) => {
                        handleUpdateSuccess(updatedRx);
                    },
                },
            );
        },
        500,
    );

    const renderCell = useCallback(
        (feature: Feature, columnKey: string) => {
            const value = feature.properties?.[columnKey];
            switch (columnKey) {
                case "target_rate_n":
                case "product_rate":
                case "actual_product_rate":
                    return (
                        <NumberInput
                            name={`rows.${feature.id}.${columnKey}`}
                            variant="bordered"
                            size="sm"
                            onAutosave={() =>
                                handleCellUpdate(feature, columnKey)
                            }
                            classNames={{
                                input: "p-1 h-8",
                                inputWrapper: "h-8 min-w-[80px]",
                            }}
                        />
                    );
                case "irrigated":
                    return feature.properties?.irrigated ? (
                        <FontAwesomeIcon
                            icon={faDroplet}
                            className="text-blue-500"
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faDropletSlash}
                            className="text-gray-500"
                        />
                    );
                case "end_gun":
                    return feature.properties?.end_gun ? (
                        <FontAwesomeIcon
                            icon={faCheck}
                            className="text-green-500"
                        />
                    ) : (
                        <FontAwesomeIcon
                            icon={faXmark}
                            className="text-red-500"
                        />
                    );
                case "area":
                case "speed":
                case "depth":
                case "injection_rate":
                case "actual_rate_n":
                    return <span>{value?.toFixed(2)}</span>;
                default:
                    return <span>{value === null ? "-" : value}</span>;
            }
        },
        [handleCellUpdate],
    );

    const [selectedFeatures, setSelectedFeatures] = useState<SharedSelection>(
        new Set(),
    );
    const [zoneFilter, setZoneFilter] = useState<SharedSelection>("all");
    const [treatmentFilter, setTreatmentFilter] =
        useState<SharedSelection>("all");
    const [irrigatedFilter, setIrrigatedFilter] =
        useState<SharedSelection>("all");
    const [endGunFilter, setEndGunFilter] = useState<SharedSelection>("all");
    const filteredItems = useMemo(() => {
        if (!rx.table?.features) return [];
        let filteredFeatures = rx.table.features;
        if (
            zoneFilter !== "all" &&
            Array.from(zoneFilter).length !== zones.length
        ) {
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return Array.from(zoneFilter).includes(
                    feature.properties?.zone?.toString(),
                );
            });
        }
        if (
            treatmentFilter !== "all" &&
            Array.from(treatmentFilter).length !== treatments.length
        ) {
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return Array.from(treatmentFilter).includes(
                    feature.properties?.treatment,
                );
            });
        }
        if (irrigatedFilter !== "all") {
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return Array.from(irrigatedFilter).includes(
                    feature.properties?.irrigated?.toString(),
                );
            });
        }
        if (endGunFilter !== "all") {
            const value = Array.from(endGunFilter).includes("true");
            filteredFeatures = filteredFeatures.filter((feature: Feature) => {
                return feature.properties?.end_gun === value;
            });
        }
        return filteredFeatures;
    }, [
        rx.table?.features,
        zoneFilter,
        treatmentFilter,
        irrigatedFilter,
        endGunFilter,
        zones.length,
        treatments.length,
    ]);
    const topContent = useMemo(() => {
        return (
            <RxTableToolbar
                rxId={rx.id}
                zones={zones}
                treatments={treatments}
                rxType={rx.rx_type}
                zoneFilter={zoneFilter}
                onZoneFilterChange={setZoneFilter}
                treatmentFilter={treatmentFilter}
                onTreatmentFilterChange={setTreatmentFilter}
                irrigatedFilter={irrigatedFilter}
                onIrrigatedFilterChange={setIrrigatedFilter}
                endGunFilter={endGunFilter}
                onEndGunFilterChange={setEndGunFilter}
                table={rx.table}
                plots={rx.plots}
                selectedFeatures={selectedFeatures}
                filteredItems={filteredItems}
                updateRatesRequest={updateRates}
                handleUpdateSuccess={handleUpdateSuccess}
            />
        );
    }, [
        rx.id,
        rx.rx_type,
        rx.table,
        rx.plots,
        zones,
        treatments,
        zoneFilter,
        treatmentFilter,
        irrigatedFilter,
        endGunFilter,
        selectedFeatures,
        filteredItems,
        updateRates,
        handleUpdateSuccess,
    ]);

    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column:
            zones.length > 0
                ? "zone"
                : rx.rx_type !== RxType.ZONE
                    ? "stop"
                    : "target_rate_n",
        direction: "ascending",
    });
    const sortedItems = useMemo(() => {
        const { column, direction } = sortDescriptor;
        if (!column) return filteredItems;
        const sorted = [...filteredItems].sort((a: Feature, b: Feature) => {
            const aValue = a.properties?.[column];
            const bValue = b.properties?.[column];

            // Special handling for different columns
            switch (column) {
                case "irrigated":
                case "end_gun":
                    return direction === "ascending"
                        ? aValue === bValue
                            ? 0
                            : aValue
                                ? -1
                                : 1
                        : aValue === bValue
                            ? 0
                            : aValue
                                ? 1
                                : -1;

                case "zone":
                    return direction === "ascending"
                        ? Number(aValue) - Number(bValue)
                        : Number(bValue) - Number(aValue);

                default:
                    if (
                        typeof aValue === "number" &&
                        typeof bValue === "number"
                    ) {
                        return direction === "ascending"
                            ? aValue - bValue
                            : bValue - aValue;
                    }

                    if (aValue === bValue) return 0;
                    if (aValue === undefined || aValue === null) {
                        return direction === "ascending" ? -1 : 1;
                    }
                    if (bValue === undefined || bValue === null) {
                        return direction === "ascending" ? 1 : -1;
                    }
                    return direction === "ascending"
                        ? String(aValue).localeCompare(String(bValue))
                        : String(bValue).localeCompare(String(aValue));
            }
        });
        return sorted;
    }, [filteredItems, sortDescriptor]);

    return (
        <FormProvider {...form}>
            <form>
                <Table
                    removeWrapper={true}
                    isStriped={true}
                    selectionMode="multiple"
                    color="primary"
                    radius="none"
                    isCompact={true}
                    isHeaderSticky={true}
                    sortDescriptor={sortDescriptor}
                    onSortChange={setSortDescriptor}
                    topContent={topContent}
                    topContentPlacement="outside"
                    selectedKeys={selectedFeatures}
                    onSelectionChange={setSelectedFeatures}
                >
                    <TableHeader columns={columns}>
                        {(column) => (
                            <TableColumn
                                key={column.key}
                                allowsSorting
                                minWidth={80}
                            >
                                {column.label}
                            </TableColumn>
                        )}
                    </TableHeader>
                    <TableBody items={sortedItems}>
                        {(item) => (
                            <TableRow key={item.id}>
                                {(columnKey) => (
                                    <TableCell>
                                        {renderCell(item, columnKey.toString())}
                                    </TableCell>
                                )}
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </form>
        </FormProvider>
    );
}

export default RxTable;
